class StickyForm extends HTMLElement {
  constructor() {
    super();
  }

  els() {
    this.bisEl = this.querySelector('[data-bis-button]');
    this.submitEl = this.querySelector('[data-product-add-to-cart]');
    this.pdpEl = document.querySelector('product-detail');
    this.pdpBtnEl = this.pdpEl?.querySelector('[data-product-form]');
    // Assumes a single select (Size). Combined listings skip the Color select in the sticky form, and as of
    // MCNT-30 there are no non-combined-listing products with multiple options. If one is added, every select
    // here needs binding/syncing from currentVariant.options by position
    this.selectEl = this.querySelector('select');

    if (this.selectEl) this.selectElOnchange();
  }

  pauseUnsubscriber = undefined;
  startUnsubscriber = undefined;
  updateUnsubscriber = undefined;

  connectedCallback() {
    this.els();
    this.io();

    this.pauseUnsubscriber = window.subscribe(PUB_SUB_EVENTS.stickyFormPause, () => {
      document.documentElement.classList.remove('sticky-form--show');
      this.observer.disconnect();
    });

    this.startUnsubscriber = window.subscribe(PUB_SUB_EVENTS.stickyFormStart, () => {
      this.els();
      if (!this.observer) {
        this.io();
      } else if (this.pdpBtnEl) {
        this.observer.observe(this.pdpBtnEl);
      }
    });

    this.updateUnsubscriber = window.subscribe(PUB_SUB_EVENTS.stickyFormUpdate, e => {
      this.currentVariant = e.currentVariant;
      this.updateForm();
      this.updatePrice();
    });
  }

  disconnectedCallback() {
    const unsubscribers = [
      this.pauseUnsubscriber,
      this.startUnsubscriber,
      this.updateUnsubscriber,
    ];

    unsubscribers.forEach(unsubscriber => unsubscriber && unsubscriber());
  }

  io() {
    if (!this.pdpBtnEl) return;

    const callback = entries => {
      entries.forEach(entry => {
        // with show-below-form, a button still below the viewport (not yet reached) keeps the bar hidden
        const isAbove = entry.boundingClientRect.top < 0;
        const show = !entry.isIntersecting && (!this.hasAttribute('data-show-below-form') || isAbove);
        document.documentElement.classList.toggle('sticky-form--show', show);
      });
    };

    this.observer = new IntersectionObserver(callback, {});
    this.observer.observe(this.pdpBtnEl);
  }

  selectElOnchange() {
    // els() runs again on every stickyFormStart, only bind once per select element
    if (!this.pdpEl || this.selectEl.dataset.stickyBound) return;
    this.selectEl.dataset.stickyBound = 'true';

    // setting selectEl.value from updateForm doesn't fire change, so this only runs on user input
    this.selectEl.addEventListener('change', e => {
      const value = e.target.value;
      if (!value) return;

      const pdpInputEl = this.pdpEl.querySelector(`variant-radios input[type="radio"][value="${CSS.escape(value)}"]`);
      if (pdpInputEl) {
        pdpInputEl.checked = true;
        this.pdpEl.querySelector('variant-radios')?.onVariantChange();
      }

      const pdpSelectEl = this.pdpEl.querySelector('select[name="options[Size]"]');
      if (pdpSelectEl) {
        pdpSelectEl.value = value;
        this.pdpEl.querySelector('variant-selects')?.onVariantChange();
      }
    });
  }

  updateBis() {
    if (!this.bisEl || !this.currentVariant) return;

    if (this.currentVariant.available) {
      this.bisEl.classList.add('hide');
      this.submitEl.classList.remove('hide');
    } else {
      this.bisEl.classList.remove('hide');
      this.submitEl.classList.add('hide');
    }
  }

  updateForm() {
    const form = this.querySelector('form');
    this.pdpForm = this.pdpEl?.querySelector('form');
    if (!form || !this.pdpForm) return;

    // Update input value
    const inputEl = form.querySelector('[name="id"]');
    const pdpInputEl = this.pdpForm.querySelector('[name="id"]');
    if (inputEl && pdpInputEl) inputEl.value = pdpInputEl.value;

    this.updateBis();

    // variant pickers only update buttons within their own product-detail/sticky-form, so sync this add button here
    const stickyPicker = this.querySelector('variant-selects, variant-radios');
    if (stickyPicker && this.currentVariant) {
      stickyPicker.currentVariant = this.currentVariant;
      stickyPicker.toggleAddButton();
    }

    // update selected option: pick whichever of the variant's option values exists in the sticky select,
    // so it works regardless of option order or count
    if (!this.selectEl || !this.currentVariant?.options) return;

    const variantOptions = this.currentVariant.options;
    const selectedOption = Array.from(this.selectEl.options).find(option => variantOptions.includes(option.value));
    if (selectedOption) this.selectEl.value = selectedOption.value;
  }

  updatePrice() {
    // re-render this section's price for the selected variant so badges, sale and final sale follow its settings
    const priceEl = this.querySelector('[data-sticky-price]');
    if (!priceEl || !this.currentVariant) return;

    const variantId = this.currentVariant.id;
    fetch(`${window.Shopify?.routes?.root || '/'}products/${this.dataset.productHandle}?variant=${variantId}&section_id=${this.dataset.id}`)
      .then(response => response.text())
      .then(responseText => {
        // a newer selection's request may have finished first
        if (this.currentVariant?.id !== variantId) return;
        const source = new DOMParser().parseFromString(responseText, 'text/html').querySelector('[data-sticky-price]');
        if (source) priceEl.innerHTML = source.innerHTML;
      })
      .catch(error => console.error('Error updating sticky price:', error));
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('sticky-form')) {
    customElements.define('sticky-form', StickyForm);
  }
});
